import type { ReactNode } from 'react';

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageShellProps {
  children: ReactNode;
  pageTitle: string;
  subtitle?: string;
  actions?: ReactNode;
  breadcrumbs?: Breadcrumb[];
}

/**
 * Ersetzt das `AppShell` aus dem hauseigenen Schattenpaket
 * `@weretradeit/design-system@0.1.0`, das mit dem echten Paket nur den Namen
 * teilte: das eine liefert React-Bauteile, das andere ausschliesslich CSS.
 *
 * Der auffaelligste Unterschied ist, was hier NICHT mehr steht. Das alte
 * AppShell rief `<Header>` und `<Footer>` selbst — waehrend App.tsx um die
 * Routen herum bereits `<AppHeader>` und `<Footer>` rendert. Jede Seite ausser
 * `/` trug damit ZWEI Kopfzeilen und ZWEI Fusszeilen uebereinander. Die
 * aeussere gewinnt, die innere schob den Inhalt nach unten.
 *
 * Uebrig bleibt, was eine Seite wirklich beisteuert: Brotkrumen, Titel,
 * Untertitel und Aktionen. Alle Klassen stehen im Design-System v3
 * (`layouts/app-shell.css`, `components/navigation.css`) — keine davon ist
 * hier erfunden.
 */
export const PageShell = ({ children, pageTitle, subtitle, actions, breadcrumbs }: PageShellProps) => (
  <main className="app-main">
    <div className="page">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="breadcrumb" aria-label="Brotkrumen">
          {breadcrumbs.map((crumb, i) => (
            <span key={`${crumb.label}-${i}`}>
              {crumb.href ? (
                <a href={crumb.href}>{crumb.label}</a>
              ) : (
                /* Die letzte Krume ist die aktuelle Seite. `aria-current`
                   faerbt sie ueber `.breadcrumb [aria-current="page"]` und
                   sagt sie Vorlesegeraeten an — beides haette eine blosse
                   Farbregel nicht geleistet. */
                <span aria-current="page">{crumb.label}</span>
              )}
              {i < breadcrumbs.length - 1 && <span className="breadcrumb__sep"> / </span>}
            </span>
          ))}
        </nav>
      )}

      <div className="page-header">
        <div>
          <h1>{pageTitle}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>

      {children}
    </div>
  </main>
);
